import { Box, IconButton, TextField, MenuItem, Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';

export const FAROL_OPCOES = [
  { value: '', label: '—' },
  { value: 'azul', label: 'Planejada' },
  { value: 'amarelo', label: 'Em andamento' },
  { value: 'verde', label: 'Concluída' },
  { value: 'vermelho', label: 'Atraso' },
];

// Cores dos blocos da grade de horários da Programação (as mesmas cinco do PDF).
export const COR_BLOCO_OPCOES = [
  { value: '', label: 'Padrão (branco)', cor: '#FFFFFF' },
  { value: 'azul', label: 'Azul', cor: '#DBE7F7' },
  { value: 'cinza', label: 'Cinza', cor: '#C9C9C9' },
  { value: 'branco', label: 'Branco', cor: '#FFFFFF' },
  { value: 'laranja', label: 'Laranja', cor: '#F6A21E' },
  { value: 'amarelo', label: 'Amarelo', cor: '#FCD34D' },
];

export const RISCO_OPCOES = [
  { value: '', label: '—' },
  { value: 'baixo', label: 'Baixo' },
  { value: 'medio', label: 'Médio' },
  { value: 'alto', label: 'Alto' },
];

/**
 * Lista de itens editável — usada nas seções do Relatório de Visita
 * (Entregas Planejadas, Executadas, Oportunidades, Equipe, Programação).
 *
 * `campos` descreve quais caixas aparecem em cada linha, ex:
 *   [{ nome: 'descricao', label: 'Atividade', flex: 3, multiline: true },
 *    { nome: 'farol', label: 'Status', tipo: 'select', opcoes: FAROL_OPCOES }]
 *
 * Campo do tipo 'hora': caixa de horário (HH:MM). Campo 'select' cuja opção tem `cor`: mostra um
 * quadradinho dessa cor antes do nome.
 *
 * Campo do tipo 'foto': mostra as fotos que o item já tem (item.fotos), com um X para removê-las
 * (o id vai para item._fotosRemover e a tela exclui só depois de salvar), e o botão para
 * escolher uma foto nova (item.foto).
 */
export default function ListaItensEditavel({ itens, onChange, campos, itemVazio, textoBotao = 'Adicionar' }) {
  const adicionar = () => {
    onChange([...itens, { ...itemVazio, _key: Date.now() + Math.random() }]);
  };

  const remover = (key) => {
    onChange(itens.filter((it) => it._key !== key));
  };

  const atualizarVarios = (key, mudancas) => {
    onChange(itens.map((it) => (it._key === key ? { ...it, ...mudancas } : it)));
  };

  const atualizar = (key, campo, valor) => atualizarVarios(key, { [campo]: valor });

  const removerFotoExistente = (item, foto) => {
    atualizarVarios(item._key, {
      fotos: (item.fotos || []).filter((f) => f.id !== foto.id),
      _fotosRemover: [...(item._fotosRemover || []), foto.id],
    });
  };

  return (
    <Box>
      {itens.map((item) => (
        <Box
          key={item._key}
          sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5, p: 1.5, border: '1px solid #eee', borderRadius: 1 }}
        >
          <Box sx={{ flex: 1, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {campos.map((campo) =>
              campo.tipo === 'foto' ? (
                <Box key={campo.nome} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {(item.fotos || []).map((foto) => (
                    <Box key={foto.id} sx={{ position: 'relative', width: 44, height: 44 }}>
                      <Box
                        component="img"
                        src={foto.imagem_url}
                        alt={foto.descricao || 'Foto do item'}
                        sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 1, border: '1px solid #ddd' }}
                      />
                      <IconButton
                        size="small"
                        aria-label="Remover foto"
                        onClick={() => removerFotoExistente(item, foto)}
                        sx={{
                          position: 'absolute', top: -8, right: -8, width: 18, height: 18,
                          backgroundColor: '#fff', border: '1px solid #ddd',
                          '&:hover': { backgroundColor: '#fee' },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Box>
                  ))}
                  <Button size="small" variant="outlined" component="label" startIcon={<AddPhotoAlternateIcon />}>
                    {item[campo.nome] ? 'Trocar foto' : 'Adicionar foto'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => atualizar(item._key, campo.nome, e.target.files[0] || null)}
                    />
                  </Button>
                  {item[campo.nome] && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 160 }}>
                      {item[campo.nome].name}
                    </Typography>
                  )}
                </Box>
              ) : campo.tipo === 'select' ? (
                <TextField
                  key={campo.nome}
                  select
                  size="small"
                  label={campo.label}
                  value={item[campo.nome] || ''}
                  onChange={(e) => atualizar(item._key, campo.nome, e.target.value)}
                  sx={{ minWidth: campo.largura || 130 }}
                >
                  {campo.opcoes.map((op) => (
                    <MenuItem key={op.value} value={op.value}>
                      {op.cor && (
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block', width: 14, height: 14, mr: 1, borderRadius: '3px',
                            backgroundColor: op.cor, border: '1px solid #9AA4B2', verticalAlign: 'middle',
                          }}
                        />
                      )}
                      {op.label}
                    </MenuItem>
                  ))}
                </TextField>
              ) : campo.tipo === 'hora' ? (
                <TextField
                  key={campo.nome}
                  type="time"
                  size="small"
                  label={campo.label}
                  value={item[campo.nome] || ''}
                  onChange={(e) => atualizar(item._key, campo.nome, e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ minWidth: campo.largura || 110 }}
                />
              ) : (
                <TextField
                  key={campo.nome}
                  size="small"
                  label={campo.label}
                  value={item[campo.nome] || ''}
                  multiline={!!campo.multiline}
                  minRows={campo.multiline ? 1 : undefined}
                  maxRows={campo.multiline ? 6 : undefined}
                  onChange={(e) => atualizar(item._key, campo.nome, e.target.value)}
                  sx={{ flex: campo.flex || 'initial', minWidth: campo.largura || 130 }}
                />
              )
            )}
          </Box>
          <IconButton size="small" color="error" onClick={() => remover(item._key)} sx={{ mt: 0.5 }} aria-label="Remover linha">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={adicionar} sx={{ color: '#1a1a2e' }}>
        {textoBotao}
      </Button>
    </Box>
  );
}
